import React, { useState, useMemo } from 'react';
import { Search, X } from 'lucide-react';

/**
 * BarraAgende — quali agende stanno in griglia.
 *
 * Sostituisce il vecchio pannello a comparsa "Colonne": con quaranta
 * agende serve poterle cercare e filtrare, non scorrerle.
 *
 * La spunta governa due cose diverse a seconda di chi si tocca:
 * togliendola a chi lavora oggi si nasconde la colonna, mettendola a
 * chi non lavora la si aggiunge (e la griglia si ricarica, perche' i
 * suoi appuntamenti li conosce solo il server).
 *
 * DAL 6/10/2026 (stile «Console») non e' piu' una colonna fissa a sinistra
 * della griglia ma un riquadro che si apre dal pulsante «Agende» della
 * testata: col menu a sinistra e il pannello dell'appuntamento a destra, una
 * terza colonna lasciava alla griglia meno di cento pixel per agenda. Chiuso
 * resta nella pagina, nascosto: le spunte toccate continuano a trattenere
 * la ricarica automatica come prima (utils/versioneInLinea.js), perche' la
 * scelta delle colonne non sta nell'indirizzo e una ricarica la perderebbe.
 * Esc lo chiude da dovunque sia il fuoco: lo ascolta l'Agenda
 * (useChiudiConEsc), non questo riquadro, che col fuoco sul pulsante
 * «Agende» - aperto col mouse - non lo sentiva.
 */
const BarraAgende = ({ tutte, idsInGriglia, nascoste, extra, onCommuta, colore, aperta, onChiudi }) => {
    const [cerca, setCerca] = useState('');
    const [spec, setSpec] = useState('');
    const [soloOggi, setSoloOggi] = useState(true);

    const specializzazioni = useMemo(() => {
        const s = new Set();
        tutte.forEach((a) => { if (a.specializzazione) s.add(a.specializzazione); });
        return [...s].sort();
    }, [tutte]);

    const elenco = useMemo(() => {
        const q = cerca.trim().toLowerCase();
        return tutte.filter((a) => {
            if (soloOggi && !idsInGriglia.includes(a.id) && !extra.includes(a.id)) return false;
            if (spec && a.specializzazione !== spec) return false;
            if (!q) return true;
            return `${a.cognome || ''} ${a.medico_nome || ''} ${a.nome}`.toLowerCase().includes(q);
        });
    }, [tutte, cerca, spec, soloOggi, idsInGriglia, extra]);

    return (
        <div className="ag-barra" id="ag-barra-agende" role="region" aria-label="Agende in griglia"
             hidden={!aperta}>
            <div className="ag-barra-testa">
                <b>Agende</b>
                <button type="button" onClick={onChiudi} aria-label="Chiudi le agende">
                    <X size={16} aria-hidden="true" />
                </button>
            </div>

            {/* Il filtro e la ricerca restringono l'elenco qui sotto: non
                trattengono la ricarica automatica (utils/versioneInLinea.js).
                Le spunte delle agende si', di proposito: vedi in testa. */}
            <select value={spec} onChange={(e) => setSpec(e.target.value)} className="ag-barra-spec"
                    data-non-trattiene aria-label="Specializzazione">
                <option value="">Tutte le specializzazioni</option>
                {specializzazioni.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>

            <div className="ag-barra-cerca" data-non-trattiene>
                <Search size={14} aria-hidden="true" />
                <input
                    value={cerca}
                    onChange={(e) => setCerca(e.target.value)}
                    placeholder="Cerca per agenda"
                    aria-label="Cerca per agenda"
                />
            </div>

            <div className="ag-barra-oggi">
                <span id="ag-barra-oggi-etichetta">Lavorano oggi</span>
                <button
                    type="button"
                    role="switch"
                    aria-checked={soloOggi}
                    aria-labelledby="ag-barra-oggi-etichetta"
                    className={`ag-switch ${soloOggi ? 'on' : ''}`}
                    onClick={() => setSoloOggi(!soloOggi)}
                >
                    <span />
                </button>
            </div>

            <div className="ag-barra-lista">
                {!elenco.length && <p className="ag-barra-vuoto">Nessuna agenda</p>}
                {elenco.map((a) => {
                    const dentro = (idsInGriglia.includes(a.id) || extra.includes(a.id))
                        && !nascoste.includes(a.id);
                    const c = colore(a.id);
                    return (
                        <label key={a.id} className="ag-barra-voce" data-agenda={a.id}>
                            <input
                                type="checkbox"
                                checked={dentro}
                                onChange={(e) => onCommuta(a.id, e.target.checked)}
                            />
                            <span
                                className={`ag-barra-quadro ${dentro ? 'ag-barra-quadro--pieno' : ''}`}
                                style={c ? { '--ag-colore': c } : undefined}
                            />
                            <span className="ag-barra-nome">
                                {a.cognome ? `${a.cognome}, ${a.medico_nome}` : a.nome}
                            </span>
                            {!a.ha_orari && <em title="Nessun orario impostato">·</em>}
                        </label>
                    );
                })}
            </div>
        </div>
    );
};

export default BarraAgende;
